import { Button, Dialog } from '@mui/material';
import { colorSet } from '@theme';
import { IImageListItem, ISequenceCutListItem } from '@types';
import { useFetchImageDetail, useFetchSequenceCutList, useFetchSimilarImageList } from '@api';
import { useEffect, useRef, useState } from 'react';
import { Text, SecureImage, CollectionSelectModal, CollectionUpsertModal, IssueReportModal } from '..';
import { useAdultsOnlyActions, useComicActions, useMetaTagActions } from '@remote-hooks';
import {
  FemaleBodyChildOptionValueEnum,
  PathnameEnum,
  SexualPositionChildOptionValueEnum,
  ToastMessageEnum,
  StandardShotChildOptionValueEnum,
  HorizontalAngleChildOptionValueEnum,
  VerticalAngleChildOptionValueEnum,
  AgeRangeChildOptionValueEnum,
  MainMotionPartChildOptionValueEnum,
  PostureCategoryChildOptionValueEnum,
  StandardFacialExpressionChildOptionValueEnum,
  SexualBodyPartChildOptionValueEnum,
  SelectedComicOptionEnum,
  QueryParamKeyEnum
} from '@constant';
import { downloadImage, getEnumKeyByValue } from '@utils';
import { useCollection, useIssueReport, useToast } from '@hooks';
import { useLocation, useNavigate } from 'react-router';
import {
  ArrowLeftGray300Icon,
  ArrowLeftPrimaryIcon,
  BookmarkGray400Icon,
  BookmarkWhiteFullIcon,
  CheckGray400Icon,
  CheckWhiteIcon,
  ChevronLeftGray300Icon,
  ChevronLeftGray500Icon,
  ChevronRightGray300Icon,
  ChevronRightGray500Icon,
  ComicNameSearchIcon,
  DownloadWhiteIcon,
  FlagErrorIcon,
  SequenceIcon,
  XBlackIcon
} from '@assets/iconComponents';

interface IImageModal {
  onClose: () => void;
  onUseButtonClick?: (imageId: number, isUsed: boolean, usedCount: number) => void;
  createQueryStringForImageList?: () => string;
}

export const ImageModal = (props: IImageModal) => {
  const { onClose, onUseButtonClick, createQueryStringForImageList } = props;

  const {
    isCollectionSelectModalVisible,
    handleCollectionSelectModalOpen,
    handleCollectionSelectModalClose,
    isCollectionUpsertModalVisible,
    showGoBackButton,
    handleCollectionUpsertModalOpen,
    handleCollectionUpsertModalClose,
    handleGoBackToCollectionSelectModal
  } = useCollection();

  const { isIssueReportModalVisible, setIsIssueReportModalVisible } = useIssueReport();

  const { showToast } = useToast();

  //시퀀스 컷 관련
  const [sequenceCutList, setSequenceCutList] = useState<ISequenceCutListItem[]>([]);
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search); // location.search는 '?key=value' 형태
  const isImageModalVisible = queryParams.get(QueryParamKeyEnum.IS_IMAGE_MODAL_VISIBLE) === 'true';
  const imageIdQueryParamValue = queryParams.get(QueryParamKeyEnum.IMAGE_ID)
    ? Number(queryParams.get(QueryParamKeyEnum.IMAGE_ID))
    : null;
  const sequenceIdQueryParamValue = queryParams.get(QueryParamKeyEnum.SEQUENCE_ID)
    ? Number(queryParams.get(QueryParamKeyEnum.SEQUENCE_ID))
    : null;
  const s3UrlQueryParamValue = queryParams.get(QueryParamKeyEnum.S3_URL);

  const isSimpleModal = Boolean(!imageIdQueryParamValue && !sequenceIdQueryParamValue); //단일컷 및 시퀀스컷 이미지 상세페이지가 아닌 단순 이미지 모달
  const needSimilar = Boolean(imageIdQueryParamValue); //단일컷 또는 시퀀스컷
  const needSequence = Boolean(imageIdQueryParamValue && sequenceIdQueryParamValue); //시퀀스컷

  //이전으로 버튼 관련
  const imageIdHistoryRef = useRef<number[]>([]);
  const isPopStateRef = useRef(false);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const visibleCount = 6; // 화면에 완전히 보여지는 시퀀스 컷 리스트 아이템 갯수
  const partialVisibleCount = 7; // 잘려보이는 마지막 요소까지 포함한 시퀀스 컷 리스트 아이템 갯수

  const navigate = useNavigate();
  const locationPathname = location.pathname;
  const isStandardOutputPage = locationPathname === PathnameEnum.STANDARD_OUTPUT_PAGE;
  const isAdultsOnlyOutputPage = locationPathname === PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE;

  const { onSetComic } = useComicActions();
  const { onSetMetaTag } = useMetaTagActions();
  const { onSetAdultsOnly } = useAdultsOnlyActions();

  const { data: imageDetailData, isLoading: isImageDetailLoading } = useFetchImageDetail(
    isImageModalVisible && imageIdQueryParamValue ? imageIdQueryParamValue : undefined
  );

  //사용 버튼 관련
  const [useButtonState, setUseButtonState] = useState<{ isUsed: boolean; usedCount: number }>({
    isUsed: imageDetailData?.image.isUsed ?? false,
    usedCount: imageDetailData?.image.usedCount ?? 0
  });
  //컬렉션 버튼 관련
  const [isCollectedState, setIsCollectedState] = useState<boolean>(imageDetailData?.image.isCollected ?? false);

  const { data: sequenceCutListData } = useFetchSequenceCutList(
    isImageModalVisible && sequenceIdQueryParamValue ? sequenceIdQueryParamValue : undefined
  );

  const { data: similarImageListData } = useFetchSimilarImageList(
    isImageModalVisible && imageIdQueryParamValue ? imageIdQueryParamValue : undefined
  );

  // 모달 타이틀 텍스트값 결정 및 각각의 필터값 렌더링 여부 결정
  const isStandardImage = Boolean(imageDetailData?.imageOptionValue['배경컷'] !== undefined);
  const isAdultsOnlyImage = Boolean(imageDetailData?.imageOptionValue['체위']);

  const isGoBackButtonEnabled = () => {
    return imageIdHistoryRef.current.length > 1;
  };

  const handleGoBack = () => {
    if (isGoBackButtonEnabled()) {
      navigate(-1); // 브라우저 히스토리 한 단계 뒤로
    }
  };

  const handleUseButtonClick = () => {
    if (useButtonState.isUsed) {
      setUseButtonState({ isUsed: false, usedCount: useButtonState.usedCount - 1 });
    } else {
      setUseButtonState({ isUsed: true, usedCount: useButtonState.usedCount + 1 });
    }
  };

  // ImageModal 내 북마크 버튼 클릭 -> 컬렉션 선택 모달에서 PUT images/:id/collections 요청 성공 -> 북마크 버튼 업데이트를 위한 함수
  const handleImageCollected = (isCollected: boolean) => {
    setIsCollectedState(isCollected);
  };

  const handleComicNameSearchClick = () => {
    if (imageDetailData?.comic?.name) {
      onSetComic({
        isSectionExpanded: true,
        isToggleOn: true,
        selectedOption: SelectedComicOptionEnum.COMIC_NAME,
        genreList: [],
        comicNameList: [imageDetailData.comic.name]
      });

      if (isStandardImage) {
        if (isStandardOutputPage) {
          onClose();
        } else {
          navigate(PathnameEnum.STANDARD_OUTPUT_PAGE);
        }
      } else if (isAdultsOnlyImage) {
        if (isAdultsOnlyOutputPage) {
          onClose();
        } else {
          navigate(PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE);
        }
      }
    }
  };

  const handleTagClick = (tag: string) => {
    if (isStandardOutputPage || isAdultsOnlyOutputPage) {
      //스탠다드 검색 Output 페이지 또는 19금 Output 페이지에 있을 시 기존 옵션값을 유지
      onSetMetaTag({ selectedMetaTags: [tag] });

      //모달 닫기
      queryParams.delete(QueryParamKeyEnum.IS_IMAGE_MODAL_VISIBLE);
      queryParams.delete(QueryParamKeyEnum.IMAGE_ID);
      queryParams.delete(QueryParamKeyEnum.SEQUENCE_ID);

      const queryString = queryParams.toString();

      navigate(`${locationPathname}?${queryString}`);
    } else {
      onSetMetaTag({ selectedMetaTags: [tag] });

      //모달 닫기
      queryParams.delete(QueryParamKeyEnum.IS_IMAGE_MODAL_VISIBLE);
      queryParams.delete(QueryParamKeyEnum.IMAGE_ID);
      queryParams.delete(QueryParamKeyEnum.SEQUENCE_ID);

      const queryString = queryParams.toString();

      if (isStandardImage) {
        // 스탠다드 검색 Output 이외의 페이지에 있었을 시 스탠다드 검색 Output 페이지로 이동
        navigate(`${PathnameEnum.STANDARD_OUTPUT_PAGE}?${queryString}`);
      }

      if (isAdultsOnlyImage) {
        // 19금 Output 이외의 페이지에 있었을 시 19금 Output 페이지로 이동 & 체위값 '모든 체위'로 설정
        onSetAdultsOnly({
          sexualPosition: SexualPositionChildOptionValueEnum['모든 체위']
        });

        navigate(`${PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE}?${queryString}`);
      }
    }
  };

  const handleSequenceCutListItemClick = (imageId: number) => {
    queryParams.set(QueryParamKeyEnum.IMAGE_ID, imageId.toString());

    navigate(`${locationPathname}?${queryParams.toString()}`);
  };

  const handleSequenceCutListPrevButtonClick = () => {
    if (visibleStartIndex > 0) {
      setVisibleStartIndex((prevIndex) => (prevIndex - visibleCount > 0 ? prevIndex - visibleCount : 0));
    }
  };

  const handleSequenceCutListNextButtonClick = () => {
    if (visibleStartIndex + visibleCount < sequenceCutList.length) {
      setVisibleStartIndex((prevIndex) => prevIndex + visibleCount);
    }
  };

  const handleSequenceCutDownloadButtonClick = async () => {
    const sequenceCutDownloadList =
      sequenceCutListData && sequenceCutListData.length > 0
        ? sequenceCutListData.map((item) => {
            return { s3Url: item.s3Url, originFileName: item.originFileName };
          })
        : [];

    let isAllSuccess = true;

    for (const sequenceCut of sequenceCutDownloadList) {
      const result = await downloadImage(sequenceCut.s3Url, sequenceCut.originFileName);
      if (!result) {
        isAllSuccess = false;
      }
    }

    if (isAllSuccess) {
      showToast(ToastMessageEnum.IMAGE_DOWNLOAD_SUCCESS);
    } else {
      console.log('Failed to download some of sequence cuts');
    }
  };

  const handleSimilarImageClick = (imageId: number, sequenceId?: number) => {
    queryParams.set(QueryParamKeyEnum.IMAGE_ID, imageId.toString());
    if (sequenceId) {
      queryParams.set(QueryParamKeyEnum.SEQUENCE_ID, sequenceId.toString());
    } else {
      queryParams.delete(QueryParamKeyEnum.SEQUENCE_ID);
    }

    navigate(`${locationPathname}?${queryParams.toString()}`);
  };

  // 모달 데이터 변경 시 이전으로 버튼을 위한 히스토리 업데이트
  useEffect(() => {
    if (isImageModalVisible && imageIdQueryParamValue !== null) {
      const history = imageIdHistoryRef.current;
      const last = history[history.length - 1];

      if (isPopStateRef.current) {
        // popstate로 인한 이동은 push하지 않음
        isPopStateRef.current = false;
      } else if (imageIdQueryParamValue !== last) {
        history.push(imageIdQueryParamValue);
      }
    }
  }, [isImageModalVisible, imageIdQueryParamValue]);

  // 브라우저 뒤로가기/앞으로가기 관련
  useEffect(() => {
    const handlePopState = () => {
      // 상단 '모달 데이터 변경 시 이전으로 버튼을 위한 히스토리 업데이트' 로직 실행 방지
      isPopStateRef.current = true;

      // 컬렉션 선택 모달 닫기
      handleCollectionSelectModalClose();
      // 오류 리포트 모달 닫기
      setIsIssueReportModalVisible(false);

      // 뒤로가기시 history.pop() / 앞으로가기시 history.push()
      const currentParams = new URLSearchParams(window.location.search);
      const currentImageId = currentParams.get(QueryParamKeyEnum.IMAGE_ID)
        ? Number(currentParams.get(QueryParamKeyEnum.IMAGE_ID))
        : null;

      if (currentImageId === null) return;

      const history = imageIdHistoryRef.current;
      const lastImageId = history[history.length - 1];

      if (currentImageId === lastImageId) {
        // 동일한 값이면 변경 없음 (중복 push 방지)
        return;
      }

      // 히스토리에서 현재 imageId가 마지막으로 등장한 위치 찾기
      const lastIndex = history.lastIndexOf(currentImageId);

      if (lastIndex !== -1 && lastIndex !== history.length - 1) {
        // 뒤로 가기: 현재 값이 존재하고, 마지막이 아닌 경우 → 해당 위치까지 slice
        imageIdHistoryRef.current = history.slice(0, lastIndex + 1);
      } else {
        // 앞으로 가기: 현재 값이 없거나 마지막인 경우 → push
        imageIdHistoryRef.current.push(currentImageId);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // 모달 끌 시 이전으로 관련 히스토리 초기화
  useEffect(() => {
    if (!isImageModalVisible) {
      imageIdHistoryRef.current = [];
      isPopStateRef.current = false;
    }
  }, [isImageModalVisible]);

  // 사용 및 컬렉션 버튼 상태 initialize
  useEffect(() => {
    setUseButtonState({
      isUsed: imageDetailData?.image.isUsed ?? false,
      usedCount: imageDetailData?.image.usedCount ?? 0
    });
    setIsCollectedState(imageDetailData?.image.isCollected ?? false);
  }, [imageDetailData]);

  //시퀀스 이미지 리스트에서 선택된 컷을 찾아 isFocused: true 처리
  useEffect(() => {
    if (isImageModalVisible && imageDetailData && sequenceCutListData && sequenceCutListData.length > 0) {
      // imageDetailData.image.id와 같은 id를 가진 요소의 인덱스 찾기
      const selectedImageIndex = sequenceCutListData.findIndex((item) => item.id === imageDetailData?.image?.id);

      //imageList state에 isFocused 프로퍼티 추가
      const updatedImageList = sequenceCutListData.map((item, index) => ({
        ...item, // 기존의 속성들을 그대로 복사
        isFocused: index === selectedImageIndex // 찾은 인덱스와 일치하는 경우에만 isFocused를 true로 설정
      }));
      setSequenceCutList([...updatedImageList]);

      //visibleCount보다 이후 이미지라면 페이지 넘김 처리
      const pageIndex = Math.floor(selectedImageIndex / visibleCount) * visibleCount;
      setVisibleStartIndex(pageIndex);
    } else {
      setSequenceCutList([]);
    }
  }, [isImageModalVisible, sequenceCutListData, imageDetailData]);

  return (
    <>
      <Dialog
        open={isImageModalVisible}
        onClose={onClose}
        scroll="body"
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          },
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: 'calc(100% - 288px)',
            minHeight: 'calc(100% - 80px)',
            height: isSimpleModal ? 'calc(100% - 80px)' : 'auto',
            maxHeight: isSimpleModal ? 'calc(100% - 80px)' : 'auto',
            margin: '40px 144px',
            padding: '60px 40px',
            boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.06)',
            borderRadius: 0
          },
          '& .MuiDialog-container': {
            // 스크롤은 가능하되 스크롤바는 숨김
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge
            '&::-webkit-scrollbar': {
              display: 'none' // Chrome, Safari
            }
          }
        }}
      >
        <div ref={scrollRef}>
          {needSimilar ? (
            <div
              onClick={() => {
                handleGoBack();
              }}
              style={{
                height: 24,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                position: 'absolute',
                top: 24,
                left: 24,
                cursor: isGoBackButtonEnabled() ? 'pointer' : 'default'
              }}
            >
              {isGoBackButtonEnabled() ? <ArrowLeftPrimaryIcon /> : <ArrowLeftGray300Icon />}
              <Text variant="body1" color={isGoBackButtonEnabled() ? colorSet.primaryMain : colorSet.gray300}>
                이전으로
              </Text>
            </div>
          ) : null}
          <XBlackIcon
            style={{ width: 24, height: 24, position: 'absolute', top: 24, right: 24, cursor: 'pointer' }}
            onClick={onClose}
          />
        </div>

        {isSimpleModal && s3UrlQueryParamValue ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '6px',
              border: `1px solid ${colorSet.gray300}`,
              backgroundColor: colorSet.gray100
            }}
          >
            <SecureImage
              imageUrl={s3UrlQueryParamValue}
              imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
        ) : null}

        {needSimilar ? (
          <div style={{ display: 'flex', flexDirection: 'row', gap: 60 }}>
            {/* 좌측: 선택된 컷 / 사용 / 북마크 / 다운로드 버튼 */}
            <div>
              {/* 선택된 컷 */}
              {isImageDetailLoading ? <div style={{ width: 540, height: 540 }} /> : null}
              <div
                style={{
                  width: 540,
                  height: 'auto',
                  borderRadius: '6px',
                  border: `1px solid ${colorSet.gray300}`,
                  backgroundColor: colorSet.gray100
                }}
              >
                <SecureImage
                  imageUrl={imageDetailData?.image.s3Url ?? ''}
                  imageStyle={{ width: 538, height: 'auto', objectFit: 'contain', borderRadius: '6px' }}
                />
              </div>
              {/* 사용 / 북마크 / 다운로드 버튼 */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginTop: '12px'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'row', gap: 12 }}>
                  <Button
                    onClick={() => {
                      handleUseButtonClick();

                      if (onUseButtonClick) {
                        onUseButtonClick(
                          imageDetailData?.image.id ?? 0,
                          useButtonState.isUsed ?? false,
                          useButtonState.usedCount ?? 0
                        );
                      }
                    }}
                    style={{
                      width: '110px',
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 10,
                      justifyContent: 'center',
                      alignItems: 'center',
                      border: useButtonState.isUsed ? 'none' : `1px solid ${colorSet.gray400}`,
                      backgroundColor: useButtonState.isUsed ? colorSet.gray600 : colorSet.white
                    }}
                  >
                    {useButtonState.isUsed ? (
                      <CheckWhiteIcon style={{ width: 20, height: 20 }} />
                    ) : (
                      <CheckGray400Icon style={{ width: 20, height: 20 }} />
                    )}
                    <Text variant="body1" color={useButtonState.isUsed ? colorSet.white : colorSet.gray600}>
                      {useButtonState.usedCount ? useButtonState.usedCount.toLocaleString() : 0}
                    </Text>
                  </Button>
                  <Button
                    onClick={() => {
                      handleCollectionSelectModalOpen();
                    }}
                    style={{
                      width: '84px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      border: isCollectedState ? 'none' : `1px solid ${colorSet.gray400}`,
                      backgroundColor: isCollectedState ? colorSet.gray600 : colorSet.white
                    }}
                  >
                    {isCollectedState ? (
                      <BookmarkWhiteFullIcon style={{ width: 20, height: 20 }} />
                    ) : (
                      <BookmarkGray400Icon style={{ width: 20, height: 20 }} />
                    )}
                  </Button>
                </div>
                <Button
                  onClick={() => {
                    downloadImage(imageDetailData?.image.s3Url, imageDetailData?.image.originFileName).then(
                      (isSuccess) => {
                        if (isSuccess) {
                          showToast(ToastMessageEnum.IMAGE_DOWNLOAD_SUCCESS);
                        }
                      }
                    );
                  }}
                  variant="contained"
                  color="primary"
                  size="large"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 10,
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <DownloadWhiteIcon />
                  다운로드
                </Button>
              </div>
            </div>
            {/* 좌측 end */}

            {/* 우측: 작품명 / 필터값 / 관련태그 / 시퀀스컷 / 유사이미지 */}
            <div style={{ width: '100%' }}>
              <div
                style={{
                  padding: 20,
                  border: `1px solid ${colorSet.gray200}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '32px'
                }}
              >
                {/* 작품명 */}
                {isImageDetailLoading ? <div style={{ height: 28 }} /> : null}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 10
                    }}
                  >
                    <Text variant="h3" color={colorSet.gray500} fontWeight="bold">
                      {isStandardImage ? '[스탠다드 검색]' : '[19금]'} {imageDetailData?.comic?.name ?? ''}
                    </Text>
                    {imageDetailData?.comic?.name ? (
                      <ComicNameSearchIcon onClick={handleComicNameSearchClick} />
                    ) : (
                      <div />
                    )}
                  </div>
                  <Button
                    onClick={() => {
                      setIsIssueReportModalVisible(true);
                    }}
                    variant="outlined"
                    color="error"
                    sx={{
                      width: '64px',
                      height: '32px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <FlagErrorIcon style={{ width: 16, height: 16 }} />
                  </Button>
                </div>

                {/* 필터값 */}
                {isImageDetailLoading ? <div style={{ height: 74 }} /> : null}
                {imageDetailData && imageDetailData.imageOptionValue ? (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '10px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    {imageDetailData?.comic?.genre ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          장르: {imageDetailData?.comic?.genre}
                        </Text>
                      </div>
                    ) : null}

                    {isStandardImage && imageDetailData?.imageOptionValue['배경컷'] ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          배경컷
                        </Text>
                      </div>
                    ) : null}

                    {isAdultsOnlyImage && imageDetailData?.imageOptionValue['체위'] ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          체위:{' '}
                          {(() => {
                            const sexualPositionValue = imageDetailData?.imageOptionValue['체위'];
                            return getEnumKeyByValue(SexualPositionChildOptionValueEnum, sexualPositionValue);
                          })()}
                        </Text>
                      </div>
                    ) : null}

                    {isAdultsOnlyImage && imageDetailData?.imageOptionValue['여체'] ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          여체:{' '}
                          {(() => {
                            const femaleBodyValue = imageDetailData?.imageOptionValue['여체'];
                            return getEnumKeyByValue(FemaleBodyChildOptionValueEnum, femaleBodyValue);
                          })()}
                        </Text>
                      </div>
                    ) : null}

                    {isAdultsOnlyImage &&
                    imageDetailData?.imageOptionValue['신체부위'] &&
                    imageDetailData?.imageOptionValue['신체부위'].length > 0 ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          신체부위:{' '}
                          {(() => {
                            const bodyPartValue = imageDetailData?.imageOptionValue['신체부위'];
                            return getEnumKeyByValue(SexualBodyPartChildOptionValueEnum, bodyPartValue) ?? '';
                          })()}
                        </Text>
                      </div>
                    ) : null}

                    {imageDetailData?.imageOptionValue['남성 인원수'] ||
                    imageDetailData?.imageOptionValue['여성 인원수'] ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          인원수:{' '}
                          {imageDetailData?.imageOptionValue['남성 인원수'] &&
                          ((typeof imageDetailData?.imageOptionValue['남성 인원수'] === 'number' &&
                            imageDetailData?.imageOptionValue['남성 인원수'] > 0) ||
                            imageDetailData?.imageOptionValue['남성 인원수'] === '4+')
                            ? `남자 ${imageDetailData?.imageOptionValue['남성 인원수']}명`
                            : ''}{' '}
                          {imageDetailData?.imageOptionValue['여성 인원수'] &&
                          ((typeof imageDetailData?.imageOptionValue['여성 인원수'] === 'number' &&
                            imageDetailData?.imageOptionValue['여성 인원수'] > 0) ||
                            imageDetailData?.imageOptionValue['여성 인원수'] === '4+')
                            ? `여자 ${imageDetailData?.imageOptionValue['여성 인원수']}명`
                            : ''}
                        </Text>
                      </div>
                    ) : null}

                    {imageDetailData?.imageOptionValue['샷 구분'] ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          프레임:{' '}
                          {(() => {
                            const shotValue = imageDetailData?.imageOptionValue['샷 구분'];
                            return getEnumKeyByValue(StandardShotChildOptionValueEnum, shotValue);
                          })()}
                        </Text>
                      </div>
                    ) : null}

                    {isStandardImage && imageDetailData?.imageOptionValue['연출'] ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          연출
                        </Text>
                      </div>
                    ) : null}

                    {isStandardImage && imageDetailData?.imageOptionValue['세로앵글'] ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          세로앵글:{' '}
                          {(() => {
                            const verticalAngleValue = imageDetailData?.imageOptionValue['세로앵글'];
                            return getEnumKeyByValue(VerticalAngleChildOptionValueEnum, verticalAngleValue);
                          })()}
                        </Text>
                      </div>
                    ) : null}

                    {isStandardImage &&
                    imageDetailData?.imageOptionValue['연령대'] &&
                    imageDetailData?.imageOptionValue['연령대'].length > 0 ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          연령대:{' '}
                          {(() => {
                            const ageRangeValue = imageDetailData?.imageOptionValue['연령대'];
                            return getEnumKeyByValue(AgeRangeChildOptionValueEnum, ageRangeValue);
                          })()}
                        </Text>
                      </div>
                    ) : null}

                    {isStandardImage &&
                    imageDetailData?.imageOptionValue['중심동작부위'] &&
                    imageDetailData?.imageOptionValue['중심동작부위'].length > 0 ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          중심 동작 부위:{' '}
                          {(() => {
                            const mainMotionPartValue = imageDetailData?.imageOptionValue['중심동작부위'];
                            return getEnumKeyByValue(MainMotionPartChildOptionValueEnum, mainMotionPartValue);
                          })()}
                        </Text>
                      </div>
                    ) : null}

                    {isStandardImage &&
                    imageDetailData?.imageOptionValue['표정'] &&
                    imageDetailData?.imageOptionValue['표정'].length > 0 ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          표정:{' '}
                          {(() => {
                            const facialExpressionValue = imageDetailData?.imageOptionValue['표정'];
                            return (
                              getEnumKeyByValue(StandardFacialExpressionChildOptionValueEnum, facialExpressionValue) ??
                              ''
                            );
                          })()}
                        </Text>
                      </div>
                    ) : null}

                    {isStandardImage && imageDetailData?.imageOptionValue['데포르메 여부'] ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          데포르메
                        </Text>
                      </div>
                    ) : null}

                    {imageDetailData?.imageOptionValue['가로앵글'] &&
                    imageDetailData?.imageOptionValue['가로앵글'].length > 0 ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          가로앵글:{' '}
                          {(() => {
                            const horizontalAngleValue = imageDetailData?.imageOptionValue['가로앵글'];
                            return (
                              getEnumKeyByValue(HorizontalAngleChildOptionValueEnum, horizontalAngleValue) ??
                              horizontalAngleValue
                            );
                          })()}
                        </Text>
                      </div>
                    ) : null}

                    {isStandardImage &&
                    imageDetailData?.imageOptionValue['동작구분'] &&
                    imageDetailData?.imageOptionValue['동작구분'].length > 0 ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          동작구분:{' '}
                          {(() => {
                            const postureCategoryValue = imageDetailData?.imageOptionValue['동작구분'];
                            return (
                              getEnumKeyByValue(PostureCategoryChildOptionValueEnum, postureCategoryValue) ??
                              postureCategoryValue
                            );
                          })()}
                        </Text>{' '}
                      </div>
                    ) : null}

                    {isStandardImage && imageDetailData?.imageOptionValue['스킨십'] ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          스킨십
                        </Text>
                      </div>
                    ) : null}

                    {isAdultsOnlyImage && imageDetailData?.imageOptionValue['세로앵글'] ? (
                      <div
                        style={{
                          height: 32,
                          border: `1px solid ${colorSet.gray300}`,
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0px 12px'
                        }}
                      >
                        <Text variant="body1" color={colorSet.gray500}>
                          세로앵글:{' '}
                          {(() => {
                            const verticalAngleValue = imageDetailData?.imageOptionValue['세로앵글'];
                            return (
                              getEnumKeyByValue(VerticalAngleChildOptionValueEnum, verticalAngleValue) ??
                              verticalAngleValue
                            );
                          })()}
                        </Text>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {/* 관련 태그 */}
                <div>
                  <Text variant="subtitle" fontWeight="bold" marginBottom="10px">
                    관련 태그
                  </Text>

                  {imageDetailData && imageDetailData.metaTag && imageDetailData.metaTag.length > 0 ? (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: `10px`,
                        width: '100%',
                        boxSizing: 'border-box'
                      }}
                    >
                      {imageDetailData.metaTag.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => {
                            handleTagClick(item.name);
                          }}
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRadius: '6px',
                            backgroundColor: colorSet.gray200,
                            padding: '8px 20px',
                            cursor: 'pointer'
                          }}
                        >
                          <Text variant="body1">{item.name}</Text>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      {isImageDetailLoading ? (
                        <div style={{ height: 20 }} />
                      ) : (
                        <div style={{ padding: '6px 10px' }}>
                          <Text variant="body1" color={colorSet.gray500}>
                            관련 태그가 없습니다.
                          </Text>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 시퀀스 컷 리스트 */}
                {needSequence && sequenceCutList && sequenceCutList.length > 0 ? (
                  <div>
                    <Text variant="subtitle" fontWeight="bold" marginBottom="10px">
                      시퀀스 컷
                    </Text>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 4px',
                        border: `1px solid ${colorSet.gray300}`,
                        borderRadius: '6px',
                        gap: 4,
                        marginBottom: '10px'
                      }}
                    >
                      {visibleStartIndex === 0 ? (
                        <ChevronLeftGray300Icon />
                      ) : (
                        <ChevronLeftGray500Icon
                          onClick={handleSequenceCutListPrevButtonClick}
                          style={{ cursor: 'pointer' }}
                        />
                      )}

                      <div
                        style={{
                          width: '100%',
                          overflow: 'hidden' //마지막 요소의 부모 영역을 벗어난 부분이 보이지 않도록
                        }}
                      >
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${partialVisibleCount}, 1fr)`,
                            gap: 4,
                            width: 'calc(100% + 60px)' //마지막 요소가 60px만 보이고 부모 영역을 벗어나 가려지도록 처리
                          }}
                        >
                          {sequenceCutList
                            .slice(visibleStartIndex, visibleStartIndex + partialVisibleCount)
                            .map((item, index) => (
                              <div
                                key={item.id}
                                onClick={() => {
                                  handleSequenceCutListItemClick(item.id);
                                  if (index === visibleCount) {
                                    handleSequenceCutListNextButtonClick();
                                  }
                                }}
                                style={{
                                  position: 'relative',
                                  width: '100%',
                                  aspectRatio: 1,
                                  cursor: 'pointer'
                                }}
                              >
                                <SecureImage
                                  imageUrl={item.s3Url}
                                  imageStyle={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '2px',
                                    border: item.isFocused
                                      ? `4px solid ${colorSet.primaryMain}`
                                      : `1px solid ${colorSet.gray300}`
                                  }}
                                />
                                {item.isFocused ? (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: 'rgba(115, 103, 240, 0.4)',
                                      borderRadius: '6px' // 부모 div와 동일한 border-radius 적용
                                    }}
                                  />
                                ) : null}
                              </div>
                            ))}
                        </div>
                      </div>

                      {visibleStartIndex + visibleCount >= sequenceCutList.length ? (
                        <ChevronRightGray300Icon />
                      ) : (
                        <ChevronRightGray500Icon
                          onClick={handleSequenceCutListNextButtonClick}
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        onClick={handleSequenceCutDownloadButtonClick}
                        variant="contained"
                        color="primary"
                        size="large"
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 10,
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <DownloadWhiteIcon />
                        시퀀스 전체 다운로드
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* 유사 이미지 더보기 */}
              <div style={{ marginTop: '24px' }}>
                <Text variant="subtitle" fontWeight="bold" marginBottom="10px">
                  유사 이미지 더보기
                </Text>

                {/* 유사 이미지 리스트 */}
                <div
                  style={
                    {
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '15px 10px',
                      '--similar-image-list-item-width': 'calc((100% - 40px) / 5)'
                    } as React.CSSProperties
                  }
                >
                  {similarImageListData && similarImageListData.length > 0 ? (
                    similarImageListData.map((item: IImageListItem) => {
                      return (
                        <div
                          key={item.id}
                          style={{
                            position: 'relative',
                            width: 'var(--similar-image-list-item-width)', // 7개 아이템에 맞는 너비 계산
                            aspectRatio: '1', // 비율을 1:1로 설정하여 정사각형 형태 유지
                            overflow: 'hidden', // 이미지가 부모 크기를 넘지 않도록 설정
                            cursor: 'pointer'
                          }}
                          onClick={() => {
                            handleSimilarImageClick(item.id, item.sequence?.id);
                          }}
                        >
                          <SecureImage
                            imageUrl={item.s3ThumbnailUrl ?? item.s3Url}
                            imageStyle={{
                              width: '100%', // 부모 div의 너비를 채우도록 설정
                              height: '100%', // 부모 div의 높이를 채우도록 설정
                              objectFit: 'cover', // 이미지 비율을 유지하면서 채우기
                              borderRadius: '6px',
                              border: `1px solid ${colorSet.gray300}`
                            }}
                          />
                          {item.sequence ? (
                            <SequenceIcon
                              style={{
                                width: 44,
                                height: 44,
                                position: 'absolute',
                                top: 4,
                                right: 0
                              }}
                            />
                          ) : null}
                        </div>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: 100,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <Text variant="body1" color={colorSet.gray500}>
                        검색된 이미지가 없습니다.
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* 우측 end */}
          </div>
        ) : null}
      </Dialog>

      <CollectionSelectModal
        isVisible={isCollectionSelectModalVisible}
        onClose={handleCollectionSelectModalClose}
        imageId={imageDetailData?.image.id}
        onCollectionUpsertModalOpen={handleCollectionUpsertModalOpen}
        createQueryStringForImageList={createQueryStringForImageList}
        onImageCollected={handleImageCollected}
      />

      <CollectionUpsertModal
        isVisible={isCollectionUpsertModalVisible}
        showGoBackButton={showGoBackButton}
        onClose={handleCollectionUpsertModalClose}
        onGoBackButtonClick={handleGoBackToCollectionSelectModal}
      />

      <IssueReportModal
        isVisible={isIssueReportModalVisible}
        onClose={() => {
          setIsIssueReportModalVisible(false);
        }}
        imageId={imageDetailData?.image.id}
      />
    </>
  );
};
